import { Fragment, type ReactNode, type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { SortableContext, rectSortingStrategy, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { type ColumnDef, type Row, flexRender, type useReactTable } from '@tanstack/react-table';
import { DragOverlay, type UniqueIdentifier } from '@dnd-kit/core';
import { Loader2 } from 'lucide-react';
import { columnWidthStyle, fixedTableClassName, fixedTableStyle } from '@/components/data-table/table-layout';
import { type DoubleClickTriggerEvent, type SelectionEventProps } from '@/components/layouts/gallery-layout';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DraggableItem } from '@/components/data-table/data-table-draggable-item';
import { useEnterToOpenAsset } from '@/hooks/data/datatable/useEnterToOpenAsset';
import { DraggedItem } from '@/components/data-table/data-table-dragged-item';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { useDragAndDrop } from '@/context/DragAndDropContext';
import { type DuplicatedAsset } from '@/types/duplicates';
import { Skeleton } from '@/components/ui/skeleton';
import { DraggableItems } from '@/types/dragDrop';
import { ColumnType } from '@/types/column';
import { type Asset } from '@/types/asset';
import { GalleryView } from '@/types';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

type GroupRenderCtx<TData> = {
  row: Row<TData>;
  index: number;
};

interface DataTableProps<TData extends Asset | DuplicatedAsset, TValue> {
  parentNodeId?: string;
  data: Array<TData>;
  galleryView: GalleryView;
  columns?: Array<ColumnDef<TData, TValue>>;
  table: ReturnType<typeof useReactTable<TData>>;
  selectedAssets: Array<{ id: string; name: string; type?: string | null }>;
  dataIds: Array<UniqueIdentifier>;
  handleClick?: (row: Row<TData>, event: SelectionEventProps) => void;
  handleRightClick?: (asset: TData) => void;
  // Always Asset, not TData: the Enter-key path (useEnterToOpenAsset, wired below) resolves its
  // target from GallerySelectionContext's Asset-typed selection, not from a TData row, so a
  // handler declared for TData specifically could never be called with it.
  handleDoubleClick?: (asset: Asset, event: DoubleClickTriggerEvent) => void;
  rowCount?: number;
  isLoading: boolean;
  isAssetsLoading?: boolean;
  selectedRows?: Array<Row<TData>>;
  isSearchingInTrash?: boolean;
  isUpload?: boolean;
  enableDnd?: boolean;
  selectionBarVisible?: boolean;
  selectionSourceRowId?: string | null;
  slots?: {
    // List view: render the header that appears before a grouped section
    renderListGroupRow?: (ctx: GroupRenderCtx<TData>) => ReactNode;
    // Grid view: render the header that appears before a grouped section
    renderGridGroupHeader?: (ctx: GroupRenderCtx<TData>) => ReactNode;
    // List view: render once, before every row — child folders/albums (DM-2517)
    renderListLeadingRows?: () => ReactNode;
    // Grid view: render once, before every item — child folders/albums (DM-2517)
    renderGridLeadingBlock?: () => ReactNode;
    /*
     * Rendered after the rows/tiles and *outside* the table, in both views (DM-2517).
     *
     * This exists so an empty state can be centred in what the user can see. Inside the list table
     * it cannot be: the table is `table-fixed` and routinely far wider than the viewport, so a
     * `<td colSpan>` centres its content on the middle of the *table* — which measured at
     * x=864–1348 in a 1200px viewport, i.e. starting behind the inspector and running off the right
     * edge. Left-aligning it instead squashed it into the corner.
     */
    renderTrailingEmptyState?: () => ReactNode;
  };
  // The list table otherwise hides itself entirely when `data` is empty; a folder holding
  // only children (no assets) still needs the table shell to render the leading rows (DM-2517).
  hasLeadingContent?: boolean;
  canReorderAssets?: boolean;
}

export const DraggableItemInView = <TData extends Asset>({
  isRow,
  row,
  scrollRef,
  assets,
  parentNodeId,
  handleClick,
  handleDoubleClick,
  handleRightClick,
  isSearchingInTrash = false,
  enableDnd,
  isUpload,
  rowCount,
  onItemMeasured,
  measuredHeight,
  canReorderAssets,
}: {
  isRow?: boolean;
  row: Row<TData>;
  scrollRef: RefObject<HTMLDivElement | null>;
  assets: Array<TData>;
  parentNodeId?: string;
  handleClick?: (row: Row<TData>, event: SelectionEventProps) => void;
  // Always Asset, not TData: the Enter-key path (useEnterToOpenAsset, wired below) resolves its
  // target from GallerySelectionContext's Asset-typed selection, not from a TData row, so a
  // handler declared for TData specifically could never be called with it.
  handleDoubleClick?: (asset: Asset, event: DoubleClickTriggerEvent) => void;
  handleRightClick?: (asset: TData) => void;
  isSearchingInTrash?: boolean;
  enableDnd: boolean;
  isUpload?: boolean;
  rowCount?: number;
  onItemMeasured?: (height: number) => void;
  measuredHeight?: number;
  canReorderAssets?: boolean;
}) => {
  const [ref, inView] = useInView({
    root: scrollRef.current,
    threshold: 0,
    triggerOnce: true,
  });

  const measureRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node && onItemMeasured && !isRow) {
        const rect = node.getBoundingClientRect();
        if (rect.height > 0) {
          onItemMeasured(rect.height);
        }
      }
    },
    [onItemMeasured, isRow],
  );

  // A fresh arrow here would re-attach the ref on every render, and re-attaching restarts the
  // observation, whose first notification renders again — an unbounded loop that also loses inView.
  const itemRef = useCallback(
    (node: HTMLTableRowElement | HTMLDivElement | null) => {
      ref(node as HTMLElement);
      measureRef(node as HTMLDivElement);
    },
    [ref, measureRef],
  );

  return inView ? (
    <DraggableItem
      ref={itemRef}
      isRow={isRow}
      row={row}
      assets={assets}
      parentNodeId={parentNodeId}
      handleClick={handleClick as (row: Row<any>, e: SelectionEventProps) => void}
      handleDoubleClick={handleDoubleClick}
      handleRightClick={handleRightClick}
      isSearchingInTrash={isSearchingInTrash}
      enableDnd={enableDnd}
      isUpload={isUpload}
      rowCount={rowCount}
      canReorderAssets={canReorderAssets}
    />
  ) : isRow ? (
    <tr id={row.original.id} data-row-id={row.original.id} ref={ref}>
      {row.getVisibleCells().map((cell) => (
        <td
          key={cell.id}
          className={cn(
            ![ColumnType.DragHandle, ColumnType.Select, ColumnType.Thumbnail as string].includes(
              (cell.column.columnDef as { accessorKey: string }).accessorKey,
            ) && 'px-2',
          )}
        >
          <Skeleton
            className={cn(
              'h-5 w-full bg-ui-panel!',
              ![ColumnType.DragHandle, ColumnType.Select, ColumnType.Thumbnail as string].includes(
                (cell.column.columnDef as { accessorKey: string }).accessorKey,
              ) && ['w-full', 'w-3/4', 'w-2/3', 'w-1/2', 'w-1/3'][Math.floor(Math.random() * 5)],
              (cell.column.columnDef as { accessorKey: string }).accessorKey === ColumnType.Thumbnail ? 'h-10' : 'h-5',
            )}
          />
        </td>
      ))}
    </tr>
  ) : (
    <div id={row.original.id} className="p-4" ref={ref} style={measuredHeight ? { height: measuredHeight } : undefined}>
      <div className="flex flex-col gap-y-1">
        <Skeleton
          className="min-h-full min-w-full bg-ui-panel!"
          style={measuredHeight ? { height: measuredHeight - 60 } : { aspectRatio: '1' }}
        />
        <div className="flex justify-between">
          <Skeleton className="h-5 w-1/2 bg-ui-panel!" />
          <Skeleton className="h-5 w-2/12 bg-ui-panel!" />
        </div>
      </div>
    </div>
  );
};

export function DataTableGridListView<TData extends Asset | DuplicatedAsset, TValue>({
  parentNodeId,
  galleryView,
  table,
  data,
  dataIds,
  handleClick,
  handleDoubleClick,
  handleRightClick,
  selectedAssets,
  rowCount,
  isAssetsLoading,
  isSearchingInTrash,
  isUpload,
  enableDnd = false,
  selectionBarVisible,
  selectionSourceRowId,
  slots,
  hasLeadingContent,
  canReorderAssets,
}: DataTableProps<TData, TValue>) {
  const tCommon = useTranslations('common');
  const { activeItem, overItem } = useDragAndDrop();
  const { getActiveUploadSession } = useUploadStore();
  const {
    session: { isAssetsProcessingCanceled },
  } = getActiveUploadSession();
  const { isAllSelected } = useBrowserStore();
  useEnterToOpenAsset(handleDoubleClick);
  const [measuredItemHeight, setMeasuredItemHeight] = useState<number | undefined>(undefined);
  const previousSelectionBarVisible = useRef(false);

  const onItemMeasured = useCallback(
    (height: number) => {
      if (!measuredItemHeight) {
        setMeasuredItemHeight(height);
      }
    },
    [measuredItemHeight],
  );

  const selectedRows = useMemo(
    () => table.getRowModel().flatRows.find((row) => row.id === activeItem?.id),
    [activeItem, table],
  );

  const outerContainerClass = table.getRowModel().rows.some((row) => row.getIsGrouped())
    ? 'flex flex-col grow'
    : data.length === 0 && slots?.renderTrailingEmptyState
      ? // No asset tiles to lay out on the grid — a flex column instead, so the trailing empty
        // state below the leading block can grow into the remaining space and center itself in it.
        // Gated on the slot actually being supplied, not just on `data.length`: this component is
        // shared by duplicates/ingestion/public-gallery layouts too, none of which pass one, and
        // the switch has nothing to center for them — it would just be an unrequested layout change.
        'relative flex flex-col h-full p-6'
      : `relative p-6 grid grid-cols-[repeat(auto-fill,minmax(max(var(--thumbnail-size),14rem),1fr))] items-start content-start h-full`;

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const isSelectionBarVisible = Boolean(selectionBarVisible);
    const wasSelectionBarVisible = previousSelectionBarVisible.current;

    previousSelectionBarVisible.current = isSelectionBarVisible;

    if (galleryView !== GalleryView.LIST || !isSelectionBarVisible || wasSelectionBarVisible || !selectionSourceRowId) {
      return;
    }

    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }

    const rowElement = scrollElement.querySelector<HTMLElement>(`[data-row-id="${selectionSourceRowId}"]`);
    if (!rowElement) {
      return;
    }

    // Only reveal the row if the bar that just mounted now overlaps it.
    if (rowElement.getBoundingClientRect().bottom <= scrollElement.getBoundingClientRect().bottom) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      rowElement.scrollIntoView({ block: 'end' });
    });

    return () => cancelAnimationFrame(frame);
  }, [galleryView, selectionBarVisible, selectionSourceRowId]);

  const pageIndex = table.getState().pagination.pageIndex;

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  }, [pageIndex]);

  return (
    <>
      {galleryView === GalleryView.LIST ? (
        <div
          ref={scrollRef}
          data-selection-scroll
          className={cn('relative h-full w-full flex-1 min-h-0 overflow-auto bg-ui-container', {
            'flex items-center justify-center': isAssetsLoading,
            // No rows to lay out — a plain flex column instead of the table's own flow, so the
            // trailing empty state below can grow into the remaining space and center itself in
            // it, rather than sitting at its own content height directly under the leading rows.
            // Gated on the slot actually being supplied — see the grid view's `outerContainerClass`
            // above for why: without a trailing empty state to center, this squashes the list
            // table's fixed-width columns down to viewport width for the other three callers of
            // this shared component that never asked for the switch.
            'flex flex-col': !isAssetsLoading && data.length === 0 && Boolean(slots?.renderTrailingEmptyState),
          })}
        >
          {isAssetsLoading ? (
            <div className="flex items-center justify-center">
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="animate-spin stroke-text-base" />
                {!isAssetsProcessingCanceled && tCommon('processingAssets')}
              </div>
            </div>
          ) : (
            (data.length !== 0 || hasLeadingContent) && (
              <Table className={fixedTableClassName} style={fixedTableStyle(table)}>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id} data-cy="table-header-row">
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className={cn(header.column.columnDef.meta?.headerClassName)}
                          style={columnWidthStyle(header.column)}
                          data-cy={`table-header-${header.column.id}`}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {slots?.renderListLeadingRows?.()}
                  <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
                    {table.getRowModel().rows.map((row, index) =>
                      row.getIsGrouped() ? (
                        <Fragment key={row.id}>
                          {slots?.renderListGroupRow?.({
                            row,
                            index,
                          })}
                          {row.getLeafRows().map((leafRow) => (
                            <DraggableItemInView
                              scrollRef={scrollRef}
                              isRow
                              key={leafRow.id}
                              row={leafRow}
                              assets={data}
                              parentNodeId={parentNodeId}
                              handleClick={handleClick}
                              handleDoubleClick={handleDoubleClick}
                              handleRightClick={handleRightClick}
                              isSearchingInTrash={isSearchingInTrash}
                              enableDnd={enableDnd}
                              isUpload={isUpload}
                              rowCount={rowCount}
                              canReorderAssets={canReorderAssets}
                            />
                          ))}
                        </Fragment>
                      ) : (
                        <DraggableItemInView
                          scrollRef={scrollRef}
                          isRow
                          key={row.id}
                          row={row}
                          assets={data}
                          parentNodeId={parentNodeId}
                          handleClick={handleClick}
                          handleDoubleClick={handleDoubleClick}
                          handleRightClick={handleRightClick}
                          isSearchingInTrash={isSearchingInTrash}
                          enableDnd={enableDnd}
                          isUpload={isUpload}
                          rowCount={rowCount}
                          canReorderAssets={canReorderAssets}
                        />
                      ),
                    )}
                  </SortableContext>
                </TableBody>
              </Table>
            )
          )}
          {/*
            `sticky left-0` pins this to the visible left edge of the horizontal scroll area, and
            `w-full` resolves against that area rather than the table's scroll width — together
            that is what makes "centred" mean centred in the view. A sibling of the <Table>, so the
            table's own width never reaches it.
          */}
          {!isAssetsLoading && slots?.renderTrailingEmptyState && (
            <div className={cn('sticky left-0 w-full', data.length === 0 && 'flex-1')} data-cy="trailing-empty-state">
              {slots.renderTrailingEmptyState()}
            </div>
          )}
        </div>
      ) : (
        <div
          ref={scrollRef}
          className={cn(
            'w-full flex-1 min-h-0 overflow-y-auto bg-ui-container',
            isAssetsLoading ? 'relative flex h-full justify-center' : outerContainerClass,
          )}
        >
          {isAssetsLoading ? (
            <div className="flex items-center justify-center">
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="animate-spin stroke-text-base" />
                {!isAssetsProcessingCanceled && tCommon('processingAssets')}
              </div>
            </div>
          ) : (
            <SortableContext items={dataIds} strategy={rectSortingStrategy}>
              {slots?.renderGridLeadingBlock?.()}
              {table.getRowModel().rows.map((row, index) =>
                row.getIsGrouped() ? (
                  <Fragment key={row.id}>
                    {slots?.renderGridGroupHeader?.({
                      row,
                      index,
                    })}
                    <div className="relative grid grid-cols-[repeat(auto-fill,minmax(max(var(--thumbnail-size),14rem),1fr))] bg-ui-container p-6">
                      {row.getLeafRows().map((leafRow) => (
                        <DraggableItemInView
                          scrollRef={scrollRef}
                          key={leafRow.id}
                          row={leafRow}
                          assets={data}
                          parentNodeId={parentNodeId}
                          handleClick={handleClick}
                          handleDoubleClick={handleDoubleClick}
                          handleRightClick={handleRightClick}
                          isSearchingInTrash={isSearchingInTrash}
                          enableDnd={enableDnd}
                          isUpload={isUpload}
                          rowCount={rowCount}
                          onItemMeasured={onItemMeasured}
                          measuredHeight={measuredItemHeight}
                          canReorderAssets={canReorderAssets}
                        />
                      ))}
                    </div>
                  </Fragment>
                ) : (
                  <DraggableItemInView
                    scrollRef={scrollRef}
                    key={row.id}
                    row={row}
                    assets={data}
                    parentNodeId={parentNodeId}
                    handleClick={handleClick}
                    handleDoubleClick={handleDoubleClick}
                    handleRightClick={handleRightClick}
                    isSearchingInTrash={isSearchingInTrash}
                    enableDnd={enableDnd}
                    isUpload={isUpload}
                    rowCount={rowCount}
                    onItemMeasured={onItemMeasured}
                    measuredHeight={measuredItemHeight}
                    canReorderAssets={canReorderAssets}
                  />
                ),
              )}
            </SortableContext>
          )}
          {/* Grid view has no horizontal overflow, so it only needs to span the grid's columns —
              except with no tiles, where the container above is a flex column and this grows
              into the remaining space instead. */}
          {!isAssetsLoading && slots?.renderTrailingEmptyState && (
            <div
              className={cn('w-full', data.length === 0 ? 'flex-1' : 'col-span-full')}
              data-cy="trailing-empty-state"
            >
              {slots.renderTrailingEmptyState()}
            </div>
          )}
        </div>
      )}
      {selectedRows && activeItem?.data.current.type === DraggableItems.ASSET && (
        <DragOverlay>
          <DraggedItem
            galleryView={galleryView}
            overItem={overItem}
            row={selectedRows}
            numberOfItems={isAllSelected && rowCount && rowCount !== 0 ? rowCount : (selectedAssets?.length ?? 1)}
          />
        </DragOverlay>
      )}
    </>
  );
}
